import { IMPORT_MSG, LIST_MSG, NUMBERING_MSG, RECURRING_MSG, INVOICE_BULK_MSG, INVOICE_PAY_TAB_LABEL, INVOICE_STATE_LABEL, SEND_STATE_LABEL, inListPeriod, type SendSummary, listQueryHit, overdueText, sendFilterHit, sendTitleOf, ymdSlash, type Invoice, type InvoiceList, type InvoiceSummary } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { useMe } from "../auth.js";
import { FilterInput, ListHead } from "../components/ListHead.jsx";
import { ListFilterBar, useListFilters } from "../components/ListFilters.jsx";
import { ListTabs, listTabPanel } from "../components/ListTabs.jsx";
import { RowMenu } from "../components/RowMenu.jsx";
import { Icon } from "../components/Shell.jsx";
import { usePageName } from "../components/BackButton.jsx";
import { ErrorText, Help, type ToastFn } from "../components/ui.jsx";
import { notify, notifyError } from "../components/Notice.jsx";
import { useModule } from "../core/slots.jsx";
import { uses } from "../core/uses.js";
import { t, useLabels } from "../labels.js";
import { todayJst, yen } from "../lib/num.js";
import { usePcOnly } from "../lib/pcOnly.js";
import { InvoiceBulkBand } from "./InvoiceBulk.jsx";

const ST: Record<InvoiceSummary["status"], string> = { draft: "下書き", issued: "発行済", sent: "送付済", paid: "入金済み", lost: "—", voided: "取り消し" };
const FILTERS = [
  ["all", "すべて"],
  ["draft", "下書き"],
  // #302（A4）：「出したもの」→「発行済」
  ["issued", "発行済"],
] as const;
/** 入金のタブ（E1、営業力 docs/08 決めごと9）。API が payCounts を返す会社（sales）だけ出す */
const PAY_FILTERS = [
  ["unpaid", INVOICE_PAY_TAB_LABEL.unpaid],
  ["overdue", INVOICE_PAY_TAB_LABEL.overdue],
  ["paid", INVOICE_PAY_TAB_LABEL.paid],
] as const;
type Filter = (typeof FILTERS)[number][0] | (typeof PAY_FILTERS)[number][0] | "recurring";

/**
 * 受注した見積から請求書を起こして、その紙へ移る（縦串5）。
 * 下書きがもうあれば（409 INVOICE_DRAFT_EXISTS）そちらをひらく。失敗の文は toast へ
 */
export function useCreateInvoice(toast: ToastFn) {
  const nav = useNavigate();
  const qc = useQueryClient();
  /** 見積から（一括）、または分割の予定の回から（042）起こす */
  return async (fromQuoteId: string, fromInstallmentId?: string) => {
    try {
      const inv = await api<Invoice>("POST", "/invoices", fromInstallmentId ? { fromInstallmentId } : { fromQuoteId });
      qc.setQueryData(["invoice", inv.id], inv);
      void qc.invalidateQueries({ queryKey: ["invoices"] });
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      void qc.invalidateQueries({ queryKey: ["estimate", fromQuoteId] });
      void qc.invalidateQueries({ queryKey: ["group"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      // 起こした直後の一言は通知（#171。前は下書きの帯「一括の請求書です。見積 … の明細を写しました。」）
      const i = inv.installment;
      const notice = i
        ? `${i.count}回のうち${i.seq}回目（${i.label}）の請求書を作成しました。`
        : `${inv.project.quoteNo ? `見積 ${inv.project.quoteNo} の明細を写しました。` : "見積の明細を写しました。"}${inv.project.invoiceCount > 0 ? `この見積の ${inv.project.invoiceCount + 1} 枚目の請求書です。` : ""}`;
      nav(`/invoices/${inv.id}`, { state: { fresh: true, notice } });
    } catch (e) {
      if (e instanceof ApiError && e.code === "INVOICE_DRAFT_EXISTS" && typeof e.problem.invoiceId === "string") {
        toast(messageOf(e), undefined, "error");
        nav(`/invoices/${e.problem.invoiceId}`);
        return;
      }
      toast(messageOf(e), undefined, "error");
    }
  };
}

/**
 * 請求書の一覧（#302 一覧の型、Figma「一覧の見直し（11/4）」③）。押すとその紙が開く。
 *   ・列：選択・請求日・番号・{customer}・{subject}・支払期限・金額（税込）・残額（入金を使う会社だけ）・状態（#302 A1。「送付」の列は4本目で）
 *   ・2行目は 分割 n／m回・締め n月分・定期 n月分（{subject}の下）と 期限超過（朱。状態の下）だけ。「見積 Q-… から」「一括」は出さない（#302 A3）
 *   ・「＋ 請求書作成」（#302 A4）：見積から（受注の見積書の一覧）／{deliveryNote}から（発行済の{deliveryNote}の一覧。使う会社だけ）／締め請求（締めの画面。sales の会社だけ）
 *   ・期間・{customer}の絞りと 絞った結果の件数・税抜・税込・残額（#302 A6）。タブの件数も絞った結果
 */
export function InvoicesPage() {
  const L = useLabels();
  usePageName("請求書");
  const nav = useNavigate();
  const me = useMe().data;
  const [sp, setSp] = useSearchParams();
  const lf = useListFilters();
  const [q, setQ] = useState("");
  const [f, setFState] = useState<Filter>(() => {
    const q = sp.get("tab");
    return q === "unpaid" || q === "overdue" || q === "paid" || q === "draft" || q === "issued" || q === "recurring" ? q : "all";
  });
  const setF = (k: Filter) => {
    setFState(k);
    setSp(
      (cur) => {
        const n = new URLSearchParams(cur);
        if (k === "all") n.delete("tab");
        else n.set("tab", k);
        return n;
      },
      { replace: true },
    );
  };
  const list = useQuery({ queryKey: ["invoices"], queryFn: () => api<InvoiceList>("GET", "/invoices") });
  const every = list.data?.items ?? [];
  // #302（A6）：{customer}・期間・絞り込みを先にかける（タブの件数も合計も絞った結果）
  const all = every.filter(
    (x) =>
      (!lf.client || x.clientId === lf.client) &&
      inListPeriod(x.issueDate, lf.range) &&
      // 送付の絞り（#303 B3）：下書きは送付を持たない＝「すべて」のときだけ出る
      (lf.send === "all" || (x.status !== "draft" && sendFilterHit(lf.send, x.send))) &&
      listQueryHit(q, x.documentNo, x.managementNo, x.clientName, x.clientFullName, x.subject, x.projectName),
  );
  const clientName = lf.client ? every.find((x) => x.clientId === lf.client)?.clientName : undefined;
  const pays = !!list.data?.payCounts;
  const thisMonth = todayJst().slice(0, 7);
  const rcOn = !!list.data?.recurringAvailable;
  const pick = (k: Filter) =>
    k === "all" ? all
    : k === "draft" ? all.filter((x) => x.status === "draft")
    : k === "issued" ? all.filter((x) => x.status !== "draft")
    : k === "unpaid" ? all.filter((x) => (x.balance ?? 0) > 0)
    : k === "overdue" ? all.filter((x) => x.payState === "overdue")
    // 今月の定期請求（#268）：請求月が今月の定期請求の請求書（下書き＋発行済）
    : k === "recurring" ? all.filter((x) => x.recurringMonth === thisMonth)
    : all.filter((x) => x.payState === "paid");
  const items = pick(f);
  const tabs: ReadonlyArray<readonly [Filter, string]> = [...FILTERS, ...(pays ? PAY_FILTERS : []), ...(rcOn ? ([["recurring", RECURRING_MSG.invoiceTab]] as const) : [])];
  // まとめて発行・PDF・メール（#267 3c）：sales の会社の PC だけ。取り消し（voided）は選べない。選んだものはタブを変えても残す
  const sales = useModule("sales");
  const bulk = sales && !usePcOnly().phone;
  const [picked, setPicked] = useState<string[]>([]);
  const selectable = (x: InvoiceSummary) => x.status !== "voided";
  const pickedRows = bulk ? every.filter((x) => picked.includes(x.id) && selectable(x)) : [];
  const shown = items.filter(selectable);
  const allShown = shown.length > 0 && shown.every((x) => picked.includes(x.id));
  const cols = (pays ? 10 : 9) + (bulk ? 1 : 0);
  const filtered = lf.active || q.trim() !== "";
  // 請求書作成の3択（#302 A4）：見積から／{deliveryNote}から（使う会社だけ）／締め請求（sales の会社だけ）
  const createItems = [
    { label: LIST_MSG.invoiceFromQuote, to: "/estimates?f=ordered", testId: "invoice-new-quote" },
    ...(sales && uses(me, "deliveryNote") ? [{ label: t(LIST_MSG.invoiceFromNotes), to: "/delivery-notes?tab=issued", testId: "invoice-new-notes" }] : []),
    ...(sales ? [{ label: LIST_MSG.invoiceClosing, to: "/closing", testId: "invoices-closing" }] : []),
  ];
  const sum = list.data
    ? {
        count: items.length,
        subtotal: items.reduce((a, x) => a + (x.subtotal ?? 0), 0),
        total: items.reduce((a, x) => a + x.total, 0),
        ...(pays ? { balance: items.reduce((a, x) => a + (x.balance ?? 0), 0) } : {}),
      }
    : null;
  return (
    <ListHead
      title={
        <>
          請求書
          <Help testId="help-invoices" label="請求書の一覧の説明" text={t("請求書は「請求書作成」から、受注した見積・{deliveryNote}・締め請求で起こします。")} />
        </>
      }
    >
      {list.isError ? <ErrorText>{messageOf(list.error)}</ErrorText> : null}
      {/* 絞り込みはタブ（9/24、幅はそろえる）。#241：タブの段は表のカードの中の上辺 */}
      <div className="lcard">
      <ListTabs
        id="il"
        label="請求書を状態で絞る"
        tabs={tabs.map(([k, label]) => ({ key: k as Filter, label, count: list.data ? pick(k as Filter).length : "…" }))}
        value={f}
        onChange={setF}
        filter={<FilterInput value={q} onChange={setQ} placeholder={t(LIST_MSG.searchDoc)} testId="il-search" />}
        // 定期請求（#268）：定期の{project}か定期の請求書のある会社だけ入口
        create={
          <span className="row il-entries">
            {rcOn ? (
              <Link className="btn tonal" to="/recurring" data-testid="invoices-recurring">
                {RECURRING_MSG.listEntry}
              </Link>
            ) : null}
            <RowMenu
              label={LIST_MSG.invoiceCreate}
              items={createItems}
              testId="invoice-new"
              triggerClass="btn primary"
              trigger={
                <>
                  <Icon k="plus" />
                  {LIST_MSG.invoiceCreate}
                </>
              }
            />
          </span>
        }
      />
      <ListFilterBar f={lf} clientName={clientName} sum={sum} sendFilter />
      <div className="tbl-wrap ltab-panel" {...listTabPanel("il", f)}>
        {pickedRows.length ? <InvoiceBulkBand picked={pickedRows} onClear={() => setPicked([])} /> : null}
        <table className="list inv-list" data-testid="invoice-list">
          <thead>
            <tr>
              {bulk ? (
                <th className="pick">
                  {shown.length ? (
                    <input
                      type="checkbox"
                      aria-label={INVOICE_BULK_MSG.pickAll}
                      checked={allShown}
                      onChange={(e) => setPicked((p) => (e.target.checked ? [...new Set([...p, ...shown.map((x) => x.id)])] : p.filter((id) => !shown.some((x) => x.id === id))))}
                      data-testid="invoice-pick-all"
                    />
                  ) : null}
                </th>
              ) : null}
              <th>{LIST_MSG.colInvoiceDate}</th>
              <th>{LIST_MSG.colNo}</th>
              <th>{L.customer}</th>
              <th>{L.subject}</th>
              <th>{LIST_MSG.colDue}</th>
              <th className="amt">{LIST_MSG.colTotal}</th>
              {pays ? <th className="amt">{LIST_MSG.colBalance}</th> : null}
              <th>{LIST_MSG.colState}</th>
              <th>{LIST_MSG.colSend}</th>
              <th className="lacts">
                <span className="sr">{LIST_MSG.rowMenu}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((x) => (
              <tr key={x.id} className="r" data-testid="invoice-row" onClick={() => nav(`/invoices/${x.id}`)} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && nav(`/invoices/${x.id}`)}>
                {bulk ? (
                  <td className="pick" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    {selectable(x) ? (
                      <input
                        type="checkbox"
                        aria-label={INVOICE_BULK_MSG.pick(x.clientName)}
                        checked={picked.includes(x.id)}
                        onChange={(e) => setPicked((p) => (e.target.checked ? [...p, x.id] : p.filter((y) => y !== x.id)))}
                        data-testid="invoice-pick"
                      />
                    ) : null}
                  </td>
                ) : null}
                <td className="num ldate">{ymdSlash(x.issueDate)}</td>
                <td className="num lno">
                  {x.documentNo ?? <span className="muted">{LIST_MSG.unissued}</span>}
                  {x.imported ? (
                    <span className="il-mgmt" data-testid="invoice-imported-mark">
                      {IMPORT_MSG.imported}
                    </span>
                  ) : x.managementNo ? (
                    <span className="il-mgmt" data-testid="invoice-management-no">
                      {NUMBERING_MSG.managementShort(x.managementNo)}
                    </span>
                  ) : null}
                </td>
                <td title={x.clientFullName ?? x.clientName}>
                  <b className="lname">{x.clientName}</b>
                </td>
                <td>
                  {x.subject || <span className="muted">（{L.subject}はまだ）</span>}
                  {x.installment ? (
                    <span className="l2" data-testid="invoice-row-split" title={x.installment.label || undefined}>
                      {LIST_MSG.split(x.installment.seq, x.installment.count)}
                    </span>
                  ) : null}
                  {x.recurringMonth ? (
                    <span className="l2" data-testid="invoice-recurring-month">
                      {LIST_MSG.recurringMonth(Number(x.recurringMonth.slice(5, 7)))}
                    </span>
                  ) : null}
                  {x.closingPeriodEnd ? (
                    <span className="l2" data-testid="invoice-closing-month">
                      {LIST_MSG.closingMonth(Number(x.closingPeriodEnd.slice(5, 7)))}
                    </span>
                  ) : null}
                </td>
                <td className="num ldate">{x.dueDate ? ymdSlash(x.dueDate) : <span className="muted">—</span>}</td>
                <td className="amt num">{x.total ? yen(x.total) : <span className="muted">—</span>}</td>
                {pays ? <td className="amt num" data-testid="invoice-balance">{x.balance != null ? yen(x.balance) : <span className="muted">—</span>}</td> : null}
                <td data-testid="invoice-state">
                  <span className={"chip lst " + (x.status === "draft" ? "quiet" : x.payState === "overdue" ? "shu" : x.payState === "paid" ? "quiet" : "ai")}>
                    {x.status === "draft" ? "下書き" : x.payState ? INVOICE_STATE_LABEL[x.payState] : ST[x.status]}
                  </span>
                  {x.payState === "overdue" && x.overdueDays ? <span className="l2 shu">{overdueText(x.overdueDays)}</span> : null}
                </td>
                {/* 送付（#303 B3）：送付の記録から。乗せると日付（取得済は取得した日と送った日） */}
                <td className="lsend" data-testid="invoice-send" title={sendTitleOf(x.send) || undefined}>
                  {x.send ? <span className={"chip lst " + (x.send.state === "none" ? "quiet" : x.send.state === "opened" ? "ai" : "")}>{SEND_STATE_LABEL[x.send.state]}</span> : <span className="muted">—</span>}
                </td>
                {/* 行の ⋯（#304）：送付を手で切り替える（出した請求書だけ）。記録に「手動」の行が残る */}
                <td className="lacts" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  {x.send ? <SendStateMenu id={x.id} send={x.send} name={x.documentNo ?? x.subject} /> : null}
                </td>
              </tr>
            ))}
            {list.data && !items.length ? (
              <tr className="emptyrows">
                <td colSpan={cols}>{filtered ? "条件に合う請求書はありません。" : all.length ? `「${tabs.find((x) => x[0] === f)?.[1] ?? ""}」の請求書はありません。` : "まだ請求書はありません。「請求書作成」の「見積から」で、受注した見積から起こせます。"}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      </div>
    </ListHead>
  );
}

/** 行の ⋯ の「送付済にする」「未送付に戻す」（#304）。押すとすぐ記録して一覧を読み直す */
function SendStateMenu({ id, send, name }: { id: string; send: SendSummary; name: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const go = async (state: "sent" | "unsent") => {
    setBusy(true);
    try {
      await api<SendSummary>("POST", `/invoices/${id}/send-state`, { state });
      await qc.invalidateQueries({ queryKey: ["invoices"] });
      notify(state === "sent" ? LIST_MSG.markedSent : LIST_MSG.markedUnsent);
    } catch (e) {
      notifyError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const items = send.state === "none" ? [{ label: LIST_MSG.markSent, onSelect: () => void go("sent"), testId: "invoice-mark-sent", disabled: busy }] : [{ label: LIST_MSG.markUnsent, onSelect: () => void go("unsent"), testId: "invoice-mark-unsent", disabled: busy }];
  return <RowMenu label={`${name}の${LIST_MSG.rowMenu}`} items={items} testId="invoice-row-menu" />;
}
